import { Link, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import Landing from "./pages/Landing";
import AppShell from "./components/AppShell";
import AppHome from "./pages/AppHome";
import QuickPage from "./pages/QuickPage";
import CasePage from "./pages/CasePage";
import LetterPage from "./pages/LetterPage";
import StructuringPage from "./pages/StructuringPage";
import HistoryPage from "./pages/HistoryPage";
import AffiliatePortal from "./pages/AffiliatePortal";
import Login from "./pages/Login";
import AuthVerify from "./pages/AuthVerify";
import Impressum from "./pages/legal/Impressum";
import Datenschutz from "./pages/legal/Datenschutz";
import AGB from "./pages/legal/AGB";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import type { ReactNode } from "react";

/**
 * Sprint H Routing-Struktur (2026-04-29):
 *
 * Public:
 *   /                   Landing (Marketing + QuickQuestion-Hook)
 *   /login              Magic-Link Login
 *   /auth/verify        Magic-Link Verify
 *   /impressum, /datenschutz, /agb
 *
 * Authenticated (unter AppShell mit Sidebar-Navigation):
 *   /app                AppHome (Tool-Tile-Grid)
 *   /app/quick          QuickPage (Schnellfrage)
 *   /app/case           CasePage (Upload + Analyse)
 *   /app/case/:caseId   CasePage mit konkretem Fall
 *   /app/letter         LetterPage (Brief-Drafter)
 *   /app/structuring    StructuringPage (Steuer-Strukturierung)
 *   /app/history        HistoryPage (Trials/Briefe/Strukturierungen)
 *   /app/affiliate      AffiliatePortal
 *
 * Legacy-Redirects (für alte Bookmarks und Deep-Links):
 *   /dashboard          → /app
 *   /affiliate          → /app/affiliate
 */
export default function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* PUBLIC — eigenes Layout mit klassischem Top-Nav */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/auth/verify" element={<AuthVerify />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
          <Route path="/agb" element={<AGB />} />
        </Route>

        {/* AUTHENTICATED — AppShell-Layout mit Sidebar */}
        <Route
          path="/app"
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route index element={<AppHome />} />
          <Route path="quick" element={<QuickPage />} />
          <Route path="case" element={<CasePage />} />
          <Route path="case/:caseId" element={<CasePage />} />
          <Route path="letter" element={<LetterPage />} />
          <Route path="structuring" element={<StructuringPage />} />
          <Route path="history" element={<HistoryPage />} />
          <Route path="affiliate" element={<AffiliatePortal />} />
        </Route>

        {/* LEGACY-REDIRECTS */}
        <Route path="/dashboard" element={<Navigate to="/app" replace />} />
        <Route path="/affiliate" element={<Navigate to="/app/affiliate" replace />} />

        <Route path="*" element={<NotFoundLayout />} />
      </Routes>
    </AuthProvider>
  );
}

function PublicLayout() {
  return (
    <div className="min-h-screen flex flex-col">
      <PublicNav />
      <main className="flex-1 w-full max-w-6xl mx-auto px-6 py-10">
        <Outlet />
      </main>
      <PublicFooter />
    </div>
  );
}

function NotFoundLayout() {
  return (
    <div className="min-h-screen flex flex-col">
      <PublicNav />
      <main className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-6xl font-bold mb-2">404</h1>
          <p className="text-slate-400 mb-6">This chamber of the court does not exist.</p>
          <Link
            to="/"
            className="inline-block rounded-lg bg-court-600 hover:bg-court-500 px-5 py-2 text-sm font-semibold transition"
          >
            Zur Startseite
          </Link>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}

/**
 * Auth-Gate: während /me noch läuft zeigt es einen Skeleton; wenn kein User →
 * Redirect auf /login mit `?next=<originalPath>` damit nach Login dorthin
 * zurückgekehrt werden kann.
 */
function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-court-500 border-t-transparent rounded-full mx-auto mb-3" />
          <p className="text-slate-500 text-sm">Sitzung wird geprüft…</p>
        </div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  }
  return <>{children}</>;
}

function PublicNav() {
  const { user, loading, logout } = useAuth();
  return (
    <header className="border-b border-slate-800/60 backdrop-blur-md sticky top-0 z-40 bg-slate-950/70">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-court-400 to-court-700 flex items-center justify-center font-bold">
            ⚖
          </div>
          <span className="font-semibold tracking-tight">Shadow Court</span>
        </Link>
        <nav className="flex items-center gap-6 text-sm text-slate-300">
          {loading ? null : user ? (
            <>
              <Link to="/app" className="hover:text-white transition">
                App öffnen
              </Link>
              <span className="text-xs text-slate-500 font-mono hidden sm:inline">
                {user.email}
              </span>
              <button
                onClick={logout}
                className="rounded border border-slate-700 hover:border-slate-500 px-3 py-1 text-xs transition"
              >
                Logout
              </button>
            </>
          ) : (
            <Link
              to="/login"
              className="rounded bg-court-600 hover:bg-court-500 px-3 py-1 text-white transition"
            >
              Login
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
}

function PublicFooter() {
  return (
    <footer className="border-t border-slate-800/60 text-slate-500 text-xs">
      <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p>© {new Date().getFullYear()} Shadow Court. Keine Rechtsberatung. Nur Mock-Jury-Simulation.</p>
        <nav className="flex items-center gap-4">
          <Link to="/impressum" className="hover:text-slate-300 transition">
            Impressum
          </Link>
          <Link to="/datenschutz" className="hover:text-slate-300 transition">
            Datenschutz
          </Link>
          <Link to="/agb" className="hover:text-slate-300 transition">
            AGB
          </Link>
        </nav>
      </div>
    </footer>
  );
}
